import { ORGANIZATION_AVATAR_PALETTE } from "../constants/organization-avatar";
import type { IOrganizationAvatar } from "./organization-avatar.types";

// NOTE: NSAI-6258 — organizations are told apart by a colour and their
// initials, because they have no logo to show: nothing in the product uploads
// one, so anything drawn here has to be derived from what an organization
// already has — its name and its uuid.
//
// Two letters, not one: "Front office" and "Finance" share an F, and a single
// letter makes two organizations look like the same badge in a list where the
// badge is the thing being scanned.
const MAX_INITIALS_LENGTH = 2;

// NOTE: the name is whatever a customer typed, so it can be one word
// ("NodeShift"), several ("Front office"), snake_case ("super_barnacle") or
// punctuation-led ("@acme"). Word characters in any script are what count as a
// letter here — `\p{L}` and `\p{N}` rather than A-Z, so a Cyrillic or Arabic
// organization gets its own initials rather than falling through to a blank.
const WORD_PATTERN = /[\p{L}\p{N}]+/gu;

// NOTE: review follow-up — a string indexes by UTF-16 CODE UNIT, so `slice(0, 2)`
// and `charAt(0)` cut a supplementary-plane letter in half and leave a lone
// surrogate, which renders as a replacement glyph. `Array.from` iterates code
// POINTS, so an organization named in a script outside the BMP gets its real
// letters back. Names are short and only the leading letters are ever wanted,
// so the intermediate array costs nothing worth saving.
const firstCodePoints = ({
  value,
  count,
}: {
  value: string;
  count: number;
}): string => Array.from(value).slice(0, count).join("");

export const getOrganizationInitials = ({ name }: { name: string }): string => {
  const words = name.match(WORD_PATTERN) ?? [];

  if (words.length === 0) return "";

  // NOTE: one word gives its first two letters ("NodeShift" -> "NO"), several
  // give the first letter of the first two ("Front office" -> "FO"), which is
  // how the reference design reads them.
  const initials =
    words.length === 1
      ? firstCodePoints({ value: words[0], count: MAX_INITIALS_LENGTH })
      : words
          .slice(0, MAX_INITIALS_LENGTH)
          .map((word) => firstCodePoints({ value: word, count: 1 }))
          .join("");

  // NOTE: review follow-up — `toUpperCase`, NOT `toLocaleUpperCase`. The latter
  // uses the HOST's locale, so a Turkish device renders "istanbul" as "İS"
  // where every other device says "IS" — and a mark that differs per device is
  // the opposite of what a recognition mark is for.
  return initials.toUpperCase();
};

// NOTE: djb2. The point is only that the same organization gets the same
// colour on every client, on every device and after every reload — so the hash
// has to be stable and defined here rather than left to each app. Renaming an
// organization must not recolour it, so this keys on the uuid; the name is
// only the fallback for a caller that has no uuid to give.
const hashKey = ({ key }: { key: string }): number => {
  let hash = 5381;

  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 33) ^ key.charCodeAt(index);
  }

  return Math.abs(hash);
};

export const getOrganizationAvatar = ({
  uuid,
  name,
}: {
  uuid?: string;
  name: string;
}): IOrganizationAvatar => {
  const paletteKey = uuid ?? name;
  const paletteIndex =
    hashKey({ key: paletteKey }) % ORGANIZATION_AVATAR_PALETTE.length;

  return {
    initials: getOrganizationInitials({ name }),
    color: ORGANIZATION_AVATAR_PALETTE[paletteIndex],
  };
};

// NOTE: NSAI-6258 — a project's mark is ONE letter where an organization's is
// two, and the client draws it in a rounded square where an organization gets a
// circle. The two ranks have to be told apart at a glance in a list that
// indents one under the other, and shape plus letter-count does that without
// spending a second colour on it: a project takes its organization's hue as a
// tint, so the tile says which organization it belongs to rather than
// competing with it.
export const getProjectInitial = ({ name }: { name: string }): string => {
  const [firstWord] = name.match(WORD_PATTERN) ?? [];

  // NOTE: code points and locale-independent casing, for the same two reasons
  // as the organization initials above.
  return firstCodePoints({ value: firstWord ?? "", count: 1 }).toUpperCase();
};
